import React from "react";
import { Clock, Dumbbell, Flame, Zap, Droplets, TrendingUp, Flag, Star, Sun, Moon, Trophy } from "lucide-react";

function Metric({ icon: Icon, label, value, unit, sub, color = "#E2E30A", testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#141414] p-4 print-day">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-200">
        <Icon className="h-3.5 w-3.5" style={{ color }} /> {label}
      </div>
      <p className="font-display text-3xl mt-2 tracking-tight text-white leading-none">
        {value}{unit && <span className="text-xs text-zinc-200 ml-1 font-sans">{unit}</span>}
      </p>
      {sub && <p className="text-[11px] text-zinc-200 mt-1.5">{sub}</p>}
    </div>
  );
}

export default function WeeklyScoreboard({ targets, trainingWeek, raceWeek }) {
  const days = targets?.daily || [];
  if (!days.length) return null;

  const totalMin = days.reduce((s, d) => s + (d.trainingMinutes || 0), 0);
  const hours = (totalMin / 60).toFixed(1);
  const allSessions = (trainingWeek?.days || []).flatMap((d) => d.sessions || []);
  const sessions = allSessions.length;
  const keySessions = allSessions.filter((s) => s.key).length;
  const heatSessions = allSessions.filter((s) => s.heat).length;
  const totalKcal = days.reduce((s, d) => s + (d.energy || 0), 0);
  const totalCarb = days.reduce((s, d) => s + (d.carb || 0), 0);
  const totalProtein = days.reduce((s, d) => s + (d.protein || 0), 0);
  const avgHydration = (days.reduce((s, d) => s + (d.hydrationMl || 0), 0) / days.length / 1000).toFixed(1);
  const peak = [...days].sort((a, b) => b.energy - a.energy)[0];
  const restDays = days.filter((d) => (d.trainingMinutes || 0) === 0).length;

  const metrics = [
    { icon: Clock, label: "Training Time", value: hours, unit: "h", sub: `${sessions} session${sessions === 1 ? "" : "s"}`, color: "#E2E30A", testId: "score-training-time" },
    { icon: Zap, label: "Week Energy", value: totalKcal.toLocaleString(), unit: "kcal", sub: `${Math.round(totalKcal / 7).toLocaleString()} /day avg`, color: "#4C9AFF", testId: "score-week-energy" },
    { icon: Flame, label: "Week Carbs", value: totalCarb.toLocaleString(), unit: "g", sub: `${Math.round(totalCarb / 7)} g/day avg`, color: "#34C759", testId: "score-week-carbs" },
    { icon: Dumbbell, label: "Week Protein", value: totalProtein.toLocaleString(), unit: "g", sub: `${Math.round(totalProtein / 7)} g/day avg`, color: "#4C9AFF", testId: "score-week-protein" },
    { icon: Droplets, label: "Avg Hydration", value: avgHydration, unit: "L/day", sub: "baseline + training", color: "#4C9AFF", testId: "score-hydration" },
    { icon: TrendingUp, label: "Peak Day", value: peak?.day?.slice(0, 3) || "—", sub: `${peak?.energy || 0} kcal`, color: "#FF9500", testId: "score-peak-day" },
  ];

  const badges = [];
  if (raceWeek?.enabled) badges.push({ icon: Flag, label: "Race Ready", color: "#FF3B30" });
  if (keySessions > 0) badges.push({ icon: Star, label: `${keySessions} Key Session${keySessions === 1 ? "" : "s"}`, color: "#E2E30A" });
  if ((targets.weeklyDemand || "").toUpperCase().includes("HIGH")) badges.push({ icon: Flame, label: "Carb Loaded", color: "#34C759" });
  if (heatSessions > 0) badges.push({ icon: Sun, label: "Heat Adapted", color: "#FF9500" });
  if (restDays >= 3) badges.push({ icon: Moon, label: "Recovery Focus", color: "#A1A1AA" });
  if (parseFloat(hours) >= 8) badges.push({ icon: Trophy, label: "Big Volume", color: "#4C9AFF" });

  return (
    <div className="mb-8" data-testid="weekly-scoreboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map((m) => <Metric key={m.testId} {...m} />)}
      </div>
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="weekly-badges">
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <span key={b.label} data-testid={`badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] uppercase tracking-wider print-day"
                style={{ borderColor: `${b.color}66`, color: b.color }}>
                <Icon className="h-3.5 w-3.5" /> {b.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
